'use es6';

import I18n from 'I18n';
import { is, List, Map as ImmutableMap, Set as ImmutableSet } from 'immutable';
import { getProperty, getPropertyLabel } from 'reporting-data/v2/dataset/datasetRecords';
import { MissingRequiredDataException } from 'reporting-data/exceptions';
import { COUNT_FIELD } from '../../field/constants';
import { groupData } from '../../highcharts/chartUtils';
import { mapLayerCategories } from './xyLayers';
import { AxisTypes, MarkTypes, Series, SeriesStyle } from './xyRecords';
import { isFormattableProperty } from '../../formatters/propertyFormatter';
const getYMethod = (yField, isAreaRange = false) => {
  if (yField === COUNT_FIELD) {
    return () => 1;
  }
  if (isAreaRange) {
    return row => {
      const y = row.get(yField);
      const val = y === null ? [null, null] : [...y.map(yVal => Number(yVal))];
      if (val.length !== 2) {
        throw new MissingRequiredDataException();
      }
      return val;
    };
  }
  return row => {
    const y = row.get(yField);
    return y === null ? null : y || 0;
  };
};

/**
 * NOTE:
 * x and y in these functions are not based on the layer, in highcharts,
 * y needs to be a linear axis, otherwise the chart is inverted with
 * x and y swapped.
 */

/**
 * Map series for a layer with only a categorical x encoding
 * This is an interesting case because theres no data displayed,
 * only scaffolding
 */
export const mapLayerSeriesX = layer => {
  const {
    id: layerId,
    dataset,
    x: {
      field: xField
    }
  } = layer;
  const {
    data
  } = dataset;
  const categories = mapLayerCategories(layer, 'x');
  const categoryLookup = ImmutableMap(categories.map(({
    key
  }) => key).map((value, index) => [value, index]));
  const seriesIndex = 0;
  const seriesId = `${layerId}-${seriesIndex}`;
  const getX = row => categoryLookup.get(row.get(xField));
  const getY = () => null;
  const getPoints = () => data.map(row => ImmutableMap({
    layerId,
    seriesId,
    row,
    value: [getX(row), getY(row)]
  }));
  return List.of(Series({
    id: seriesId,
    layerId,
    type: MarkTypes.bar,
    points: getPoints(),
    style: SeriesStyle({
      showInLegend: false
    })
  }));
};

/**
 * Map series for a layer with only a categorical x encoding
 * and a color encoding
 * This is an interesting case because theres no data displayed,
 * only scaffolding
 */
export const mapLayerSeriesXColor = layer => {
  const {
    id: layerId,
    dataset,
    x: {
      field: xField
    },
    color: {
      field: colorField,
      defined: colorDefined = ImmutableMap(),
      default: colorDefault,
      definedPatterns: colorDefinedPatterns = ImmutableMap(),
      definedDashStyles: colorDefinedDashStyles = ImmutableMap()
    }
  } = layer;
  const {
    data
  } = dataset;
  const categories = mapLayerCategories(layer, 'x');
  const series = mapLayerCategories(layer, 'color');
  const categoryLookup = ImmutableMap(categories.map(({
    key
  }) => key).map((value, index) => [value, index]));
  const getX = row => categoryLookup.get(row.get(xField));
  const getY = () => null;
  const groupedData = categories ? groupData(data, [xField, colorField]) : groupData(data, [colorField]);
  return series.map(({
    key: seriesKey,
    name
  }, index) => {
    const seriesId = `${layerId}-${index}`;
    const getPoints = () => categories.flatMap(({
      key: categoryKey
    }) => {
      let rows = groupedData.getIn([categoryKey, seriesKey]) || List();
      if (rows.isEmpty()) {
        rows = rows.push(ImmutableMap({
          [xField]: categoryKey,
          [colorField]: seriesKey
        }));
      }
      return rows.map(row => ImmutableMap({
        layerId,
        seriesId,
        row,
        value: [getX(row), getY(row)]
      }));
    });
    const points = getPoints();
    const style = ImmutableMap({
      color: colorDefined.get(String(seriesKey)) || colorDefault,
      pattern: colorDefinedPatterns.get(String(seriesKey)),
      dashStyle: colorDefinedDashStyles.get(String(seriesKey))
    });
    return Series({
      id: seriesId,
      layerId,
      name,
      type: MarkTypes.bar,
      points,
      style
    });
  });
};

/**
 * Map series for a layer with only a color encoding.
 * This is an interesting case because theres no data displayed,
 * only scaffolding
 */
export const mapLayerSeriesColor = layer => {
  const {
    id: layerId,
    color: {
      defined: colorDefined = ImmutableMap(),
      default: colorDefault,
      definedPatterns: colorDefinedPatterns = ImmutableMap(),
      definedDashStyles: colorDefinedDashStyles = ImmutableMap()
    }
  } = layer;
  const series = mapLayerCategories(layer, 'color');
  return series.map(({
    key: seriesKey,
    name
  }, index) => {
    const seriesId = `${layerId}-${index}`;
    const style = ImmutableMap({
      color: colorDefined.get(String(seriesKey)) || colorDefault,
      pattern: colorDefinedPatterns.get(String(seriesKey)),
      dashStyle: colorDefinedDashStyles.get(String(seriesKey))
    });
    return Series({
      id: seriesId,
      layerId,
      name,
      type: MarkTypes.bar,
      points: List(),
      style
    });
  });
};

/* Map series for a layer with only a linear y encoding */
export const mapLayerSeriesY = layer => {
  const {
    id: layerId,
    dataset,
    mark: {
      type: markType,
      zIndex,
      stacking,
      stack,
      marker,
      dashStyle
    },
    y: {
      field: yField,
      formattingObject,
      formattingObjectMap
    },
    color: {
      default: colorDefault,
      defaultPattern: patternDefault
    }
  } = layer;
  const {
    data: rows
  } = dataset;
  const hasProperty = !!getProperty(dataset, yField);
  const maybeFormattableProperty = hasProperty ? getProperty(dataset, yField) : ImmutableMap();
  const format = formattingObjectMap && formattingObjectMap.get(yField) || formattingObject;
  const property = isFormattableProperty(maybeFormattableProperty) ? maybeFormattableProperty.set('formattingType', format && format.type).set('formattingObject', format) : maybeFormattableProperty;
  const propertyLabel = getPropertyLabel(dataset, yField);
  const seriesIndex = 0;
  const seriesId = `${layerId}-${seriesIndex}`;
  const seriesName = propertyLabel;
  const getX = () => 0;
  const getY = getYMethod(yField);
  const getPoints = () => rows.map(row => ImmutableMap({
    layerId,
    seriesId,
    row,
    value: [getX(row), getY(row)]
  }));
  const points = getPoints();
  const forceStacking = markType === MarkTypes.bar;
  const style = ImmutableMap({
    zIndex,
    color: colorDefault,
    pattern: patternDefault,
    forceStacking,
    marker,
    dashStyle
  });
  return List.of(Series({
    id: seriesId,
    layerId,
    property,
    name: seriesName,
    type: markType,
    stacking: stacking || forceStacking,
    stack,
    points,
    style,
    metricName: yField
  }));
};

/* Map series for a layer with an x and y encoding */
export const mapLayerSeriesXY = layer => {
  const {
    id: layerId,
    dataset,
    mark: {
      type: markType,
      zIndex,
      stacking,
      stack,
      marker,
      dashStyle
    },
    x: {
      field: xField,
      type: xType
    },
    y: {
      field: yField,
      fill,
      formattingObject,
      formattingObjectMap
    },
    color: {
      default: colorDefault,
      defaultPattern: patternDefault
    }
  } = layer;
  const {
    data
  } = dataset;
  const categories = mapLayerCategories(layer, 'x');
  const categoryLookup = categories ? ImmutableMap(categories.map(({
    key
  }) => key).map((value, index) => [value, index])) : undefined;
  const hasProperty = !!getProperty(dataset, yField);
  const maybeFormattableProperty = hasProperty ? getProperty(dataset, yField) : ImmutableMap();
  const format = formattingObjectMap && formattingObjectMap.get(yField) || formattingObject;
  const property = isFormattableProperty(maybeFormattableProperty) ? maybeFormattableProperty.set('formattingType', format && format.type).set('formattingObject', format) : maybeFormattableProperty;
  const propertyLabel = getPropertyLabel(dataset, yField);
  const seriesIndex = 0;
  const seriesId = `${layerId}-${seriesIndex}`;
  const seriesName = propertyLabel;
  const isAreaRange = markType === MarkTypes.arearange;
  const getX = categoryLookup ? row => categoryLookup.get(row.get(xField)) : row => {
    const x = row.get(xField) || 0;
    return xType === AxisTypes.datetime ? I18n.moment(x).valueOf() : x;
  };
  const getY = getYMethod(yField, isAreaRange);
  const getPoints = () => {
    if (categories) {
      const groupedData = groupData(data, [xField]);
      return categories.flatMap(({
        key: categoryKey
      }) => {
        let rows = groupedData.get(categoryKey) || List();
        if (rows.isEmpty() && fill) {
          rows = rows.push(ImmutableMap({
            [xField]: categoryKey,
            [yField]: 0
          }));
        }
        return rows.map(row => {
          const value = isAreaRange ? [getX(row), ...getY(row)] : [getX(row), getY(row)];
          return ImmutableMap({
            layerId,
            seriesId,
            row,
            value
          });
        });
      });
    }
    return data.map(row => {
      const value = isAreaRange ? [getX(row), ...getY(row)] : [getX(row), getY(row)];
      return ImmutableMap({
        layerId,
        seriesId,
        row,
        value
      });
    });
  };
  const points = getPoints();
  const style = ImmutableMap({
    zIndex,
    color: colorDefault,
    pattern: patternDefault,
    forceStacking: false,
    marker,
    dashStyle
  });
  return List.of(Series({
    id: seriesId,
    layerId,
    property,
    name: seriesName,
    type: markType,
    stacking: !!stacking,
    stack,
    points,
    style,
    metricName: yField,
    levelOfDetail: [xField]
  }));
};

/* Map series for a layer with an y and color encoding */
export const mapLayerSeriesYColor = layer => {
  const {
    id: layerId,
    dataset,
    mark: {
      type: markType,
      zIndex,
      stacking,
      stack,
      marker,
      dashStyle
    },
    y: {
      field: yField,
      formattingObject,
      formattingObjectMap
    },
    color: {
      field: colorField,
      defined: colorDefined = ImmutableMap(),
      default: colorDefault,
      definedPatterns: colorDefinedPatterns = ImmutableMap(),
      definedDashStyles: colorDefinedDashStyles = ImmutableMap()
    }
  } = layer;
  const {
    data
  } = dataset;
  const series = mapLayerCategories(layer, 'color');
  const hasProperty = !!getProperty(dataset, yField);
  const maybeFormattableProperty = hasProperty ? getProperty(dataset, yField) : ImmutableMap();
  const format = formattingObjectMap && formattingObjectMap.get(yField) || formattingObject;
  const property = isFormattableProperty(maybeFormattableProperty) ? maybeFormattableProperty.set('formattingType', format && format.type).set('formattingObject', format) : maybeFormattableProperty;
  const getX = () => 0;
  const getY = getYMethod(yField);
  const groupedData = groupData(data, [colorField]);
  return series.map(({
    key: seriesKey,
    name
  }, index) => {
    const seriesId = `${layerId}-${index}`;
    const getPoints = () => {
      const rows = groupedData.get(seriesKey) || List();
      return rows.map(row => ImmutableMap({
        layerId,
        seriesId,
        row,
        value: [getX(row), getY(row)]
      }));
    };
    const points = getPoints();
    const forceStacking = markType === MarkTypes.bar;
    const style = ImmutableMap({
      zIndex,
      color: colorDefined.get(String(seriesKey)) || colorDefault,
      forceStacking,
      marker,
      pattern: colorDefinedPatterns.get(String(seriesKey)),
      dashStyle: dashStyle || colorDefinedDashStyles.get(String(seriesKey))
    });
    return Series({
      id: seriesId,
      layerId,
      property,
      name,
      type: markType,
      stacking: stacking || forceStacking,
      stack,
      points,
      style,
      metricName: yField,
      levelOfDetail: [colorField]
    });
  });
};

/* Map series for a layer with an x, y, and color encodings */
export const mapLayerSeriesXYColor = layer => {
  const {
    id: layerId,
    dataset,
    mark: {
      type: markType,
      zIndex,
      stacking,
      stack,
      marker,
      dashStyle
    },
    x: {
      field: xField,
      type: xType
    },
    y: {
      field: yField,
      fill,
      formattingObject,
      formattingObjectMap
    },
    color: {
      field: colorField,
      defined: colorDefined = ImmutableMap(),
      default: colorDefault,
      definedPatterns: colorDefinedPatterns = ImmutableMap(),
      definedDashStyles: colorDefinedDashStyles = ImmutableMap()
    }
  } = layer;
  const {
    data
  } = dataset;
  const categories = mapLayerCategories(layer, 'x');
  const series = mapLayerCategories(layer, 'color');
  const categoryLookup = categories ? ImmutableMap(categories.map(({
    key
  }) => key).map((value, index) => [value, index])) : undefined;
  const hasProperty = !!getProperty(dataset, yField);
  const maybeFormattableProperty = hasProperty ? getProperty(dataset, yField) : ImmutableMap();
  const format = formattingObjectMap && formattingObjectMap.get(yField) || formattingObject;
  const property = isFormattableProperty(maybeFormattableProperty) ? maybeFormattableProperty.set('formattingType', format && format.type).set('formattingObject', format) : maybeFormattableProperty;
  const isAreaRange = markType === MarkTypes.arearange;
  const getX = categoryLookup ? row => categoryLookup.get(row.get(xField)) : row => {
    const x = row.get(xField) || 0;
    return xType === AxisTypes.datetime ? I18n.moment(x).valueOf() : x;
  };
  const getY = getYMethod(yField, isAreaRange);
  const groupedData = categories ? groupData(data, [xField, colorField]) : groupData(data, [colorField]);

  /**
   * If the categories and series are the same set of keys,
   * we should force a stacked chart
   */
  const forceStacking = categories ? xField === colorField || is(ImmutableSet(categories.map(({
    key
  }) => key)), ImmutableSet(series.map(({
    key
  }) => key))) : false;
  return series.map(({
    key: seriesKey,
    name
  }, index) => {
    const seriesId = `${layerId}-${index}`;
    const getPoints = () => {
      if (categories) {
        return categories.flatMap(({
          key: categoryKey
        }) => {
          let rows = groupedData.getIn([categoryKey, seriesKey]) || List();
          if (rows.isEmpty() && fill) {
            rows = rows.push(ImmutableMap({
              [xField]: categoryKey,
              [colorField]: seriesKey,
              [yField]: 0
            }));
          }
          return rows.map(row => {
            const value = isAreaRange ? [getX(row), ...getY(row)] : [getX(row), getY(row)];
            return ImmutableMap({
              layerId,
              seriesId,
              row,
              value
            });
          });
        });
      }
      const seriesData = groupedData.get(seriesKey) || List();
      return seriesData.map(row => {
        const value = isAreaRange ? [getX(row), ...getY(row)] : [getX(row), getY(row)];
        return ImmutableMap({
          layerId,
          seriesId,
          row,
          value
        });
      });
    };
    const points = getPoints();
    const style = ImmutableMap({
      zIndex,
      color: colorDefined.get(String(seriesKey)) || colorDefault,
      forceStacking: forceStacking && markType === MarkTypes.bar,
      marker,
      pattern: colorDefinedPatterns.get(String(seriesKey)),
      dashStyle: dashStyle || colorDefinedDashStyles.get(String(seriesKey))
    });
    return Series({
      id: seriesId,
      layerId,
      property,
      name,
      type: markType,
      stacking: stacking || forceStacking,
      stack,
      points,
      style,
      levelOfDetail: [xField, colorField],
      metricName: yField,
      breakdownValue: seriesKey
    });
  });
};
export const mapLayerSeriesSizing = (layer, series) => {
  const {
    sizing: {
      field: sizingField
    }
  } = layer;
  const getSizing = row => {
    const sizing = row.get(sizingField);
    return sizing || 0;
  };
  return series.map(serie => serie.update('points', List([]), points => points.map(point => {
    const sizingValue = getSizing(point.get('row'));
    return point.update('value', value => value && value.concat([sizingValue]));
  })));
};
export const mapLayerSeriesDetail = (layer, series) => {
  const {
    detail: {
      field: detailField
    }
  } = layer;
  return series.map(serie => serie.update('levelOfDetail', List([]), levelOfDetail => levelOfDetail ? levelOfDetail.push(detailField) : List([detailField])).update('points', List([]), points => points.filter(point => point.hasIn(['row', detailField]))));
};